(() => {
  const STORAGE_KEY = 'splitflask-hydration-progress';

  const localDateKey = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const emitGuideEvent = (guide, action, detail = {}) => {
    const eventDetail = {
      action,
      articleHandle: guide.dataset.articleHandle || '',
      productHandle: guide.dataset.productHandle || '',
      pagePath: window.location.pathname,
      ...detail,
    };

    guide.dispatchEvent(
      new CustomEvent('splitflask:seo-guide-event', {
        bubbles: true,
        detail: eventDetail,
      })
    );

    const analyticsPublisher = window.Shopify?.analytics?.publish;
    if (typeof analyticsPublisher === 'function') {
      try {
        const publication = analyticsPublisher.call(
          window.Shopify.analytics,
          'splitflask:seo_guide_event',
          eventDetail
        );
        if (publication && typeof publication.catch === 'function') {
          publication.catch(() => {});
        }
      } catch (_error) {
        // Analytics must never interfere with the guide interaction.
      }
    }

    const eventParameters = {
      guide_action: action,
      article_handle: eventDetail.articleHandle,
      product_handle: eventDetail.productHandle,
      page_path: eventDetail.pagePath,
    };

    Object.entries(detail).forEach(([key, value]) => {
      const parameterName = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      eventParameters[parameterName] = value;
    });

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'splitflask_seo_guide', eventParameters);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'splitflask_seo_guide', ...eventParameters });
      }
    } catch (_error) {
      // Third-party analytics must never interfere with the guide interaction.
    }
  };

  const ensureProductTarget = (guide) => {
    const anchor = guide.dataset.productAnchor || 'featured-30oz-tumbler';
    const existingTarget = document.getElementById(anchor);
    if (existingTarget) return existingTarget;

    let candidate = guide.closest('.shopify-section')?.nextElementSibling || null;
    while (candidate && !candidate.classList.contains('section-highlighted-product')) {
      candidate = candidate.nextElementSibling;
    }

    const target = candidate || document.querySelector('.section-highlighted-product');
    if (target && !target.id) target.id = anchor;
    return target;
  };

  const makeHeadingId = (heading, index, usedIds) => {
    const existingId = heading.id.trim();
    if (existingId && !usedIds.has(existingId)) {
      usedIds.add(existingId);
      return existingId;
    }

    const baseId =
      heading.textContent
        .trim()
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || `section-${index + 1}`;

    let candidate = baseId;
    let suffix = 2;
    while (usedIds.has(candidate) || document.getElementById(candidate)) {
      candidate = `${baseId}-${suffix}`;
      suffix += 1;
    }

    usedIds.add(candidate);
    heading.id = candidate;
    return candidate;
  };

  const initTableOfContents = (guide) => {
    const content = guide.querySelector('[data-seo-guide-content]');
    const toc = guide.querySelector('[data-seo-toc]');
    const list = guide.querySelector('[data-seo-toc-list]');
    if (!content || !toc || !list) return;

    const headings = [...content.querySelectorAll('h2')].filter(
      (heading) => !heading.closest('.seo-guide__inline-cta')
    );
    if (headings.length < 2) return;

    const usedIds = new Set();
    const fragment = document.createDocumentFragment();

    headings.forEach((heading, index) => {
      const id = makeHeadingId(heading, index, usedIds);
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', () => {
        emitGuideEvent(guide, 'toc_click', { targetHeading: id });
      });
      item.appendChild(link);
      fragment.appendChild(item);
    });

    list.replaceChildren(fragment);
    toc.hidden = false;
  };

  const loadHydrationState = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (saved && saved.date === localDateKey()) return saved;
    } catch (_error) {
      // Storage is optional; the planner still works for the current page view.
    }
    return null;
  };

  const saveHydrationState = (state) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          date: localDateKey(),
          goal: state.goal,
          increment: state.increment,
          consumed: state.consumed,
        })
      );
    } catch (_error) {
      // Ignore disabled or full local storage.
    }
  };

  const initHydrationTracker = (guide) => {
    const tracker = guide.querySelector('[data-hydration-tracker]');
    if (!tracker) return;

    const goalSelect = tracker.querySelector('[data-hydration-goal]');
    const modeSelect = tracker.querySelector('[data-hydration-mode]');
    const progress = tracker.querySelector('[data-hydration-progress]');
    const progressBar = tracker.querySelector('[data-hydration-progress-bar]');
    const summary = tracker.querySelector('[data-hydration-summary]');
    const addButton = tracker.querySelector('[data-hydration-add]');
    const resetButton = tracker.querySelector('[data-hydration-reset]');
    if (!goalSelect || !modeSelect || !progress || !progressBar || !summary || !addButton || !resetButton) return;

    const savedState = loadHydrationState();
    const state = {
      goal: savedState?.goal || Number(goalSelect.value),
      increment: savedState?.increment || Number(modeSelect.value),
      consumed: savedState?.consumed || 0,
    };

    if ([60, 90, 120].includes(state.goal)) goalSelect.value = String(state.goal);
    if ([15, 30].includes(state.increment)) modeSelect.value = String(state.increment);

    const update = ({ persist = true } = {}) => {
      state.goal = Number(goalSelect.value);
      state.increment = Number(modeSelect.value);
      state.consumed = Math.min(Math.max(0, state.consumed), state.goal);

      const percent = Math.min(100, Math.round((state.consumed / state.goal) * 100));
      const remaining = Math.max(0, state.goal - state.consumed);
      const fillsRemaining = Math.ceil(remaining / state.increment);
      const unitLabel = state.increment === 30 ? 'full tumbler fill' : 'water-chamber fill';

      progressBar.style.width = `${percent}%`;
      progress.setAttribute('aria-valuemax', String(state.goal));
      progress.setAttribute('aria-valuenow', String(state.consumed));
      summary.textContent =
        remaining === 0
          ? `${state.consumed} of ${state.goal} oz — goal reached`
          : `${state.consumed} of ${state.goal} oz — ${fillsRemaining} ${unitLabel}${fillsRemaining === 1 ? '' : 's'} left`;
      addButton.textContent = `Add one ${state.increment} oz fill`;
      addButton.disabled = remaining === 0;

      if (persist) saveHydrationState(state);
    };

    goalSelect.addEventListener('change', () => {
      update();
      emitGuideEvent(guide, 'hydration_goal_change', { goalOunces: state.goal });
    });

    modeSelect.addEventListener('change', () => {
      update();
      emitGuideEvent(guide, 'hydration_mode_change', { fillOunces: state.increment });
    });

    addButton.addEventListener('click', () => {
      state.consumed = Math.min(state.goal, state.consumed + state.increment);
      update();
      emitGuideEvent(guide, 'hydration_fill_add', {
        goalOunces: state.goal,
        fillOunces: state.increment,
        consumedOunces: state.consumed,
      });
    });

    resetButton.addEventListener('click', () => {
      state.consumed = 0;
      update();
      emitGuideEvent(guide, 'hydration_reset', { goalOunces: state.goal });
    });

    update({ persist: false });
  };

  const initCtas = (guide) => {
    const productTarget = ensureProductTarget(guide);
    const ctas = guide.querySelectorAll('[data-seo-cta], .seo-guide__inline-cta .button');

    if (productTarget) {
      ctas.forEach((cta) => {
        if (cta.matches('[data-product-anchor-link], .seo-guide__inline-cta .button')) {
          cta.href = `#${productTarget.id}`;
        }
      });
    }

    ctas.forEach((cta) => {
      cta.addEventListener('click', () => {
        emitGuideEvent(guide, 'cta_click', {
          placement: cta.dataset.ctaPlacement || 'inline-article',
          destination: cta.getAttribute('href') || '',
        });
      });
    });
  };

  const initStickyCta = (guide) => {
    const sticky = guide.querySelector('[data-seo-sticky-cta]');
    if (!sticky) return;

    const hero = guide.querySelector('[data-seo-guide-hero]');
    const target = ensureProductTarget(guide);
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let heroPassed = false;
    let productVisible = false;

    const render = () => {
      sticky.hidden = !(mobileQuery.matches && heroPassed && !productVisible);
    };

    if ('IntersectionObserver' in window && hero) {
      const heroObserver = new IntersectionObserver(
        ([entry]) => {
          heroPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          render();
        },
        { threshold: 0 }
      );
      heroObserver.observe(hero);

      if (target) {
        const productObserver = new IntersectionObserver(
          ([entry]) => {
            productVisible = entry.isIntersecting;
            render();
          },
          { threshold: 0.08 }
        );
        productObserver.observe(target);
      }
    } else {
      const onScroll = () => {
        heroPassed = Boolean(hero && hero.getBoundingClientRect().bottom < 0);
        productVisible = Boolean(
          target && target.getBoundingClientRect().top < window.innerHeight && target.getBoundingClientRect().bottom > 0
        );
        render();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', render);
    } else {
      mobileQuery.addListener(render);
    }
    render();
  };

  const initAddToCartTracking = (guide) => {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.seoGuideTracked === 'true') return;
      form.dataset.seoGuideTracked = 'true';

      form.addEventListener('submit', () => {
        emitGuideEvent(guide, 'add_to_cart', {
          variantId: form.querySelector('[name="id"]')?.value || '',
          quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
        });
      });
    });
  };

  const initGuide = (guide) => {
    if (guide.dataset.seoGuideInitialized === 'true') return;
    guide.dataset.seoGuideInitialized = 'true';
    initTableOfContents(guide);
    initHydrationTracker(guide);
    initCtas(guide);
    initStickyCta(guide);
    initAddToCartTracking(guide);
    emitGuideEvent(guide, 'guide_view');
  };

  const initAllGuides = (root = document) => {
    root.querySelectorAll('[data-seo-guide]').forEach(initGuide);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllGuides(), { once: true });
  } else {
    initAllGuides();
  }

  document.addEventListener('shopify:section:load', (event) => initAllGuides(event.target));
})();
